import { BUFFER_HIGH_WATER, DEFAULT_CHUNK_SIZE } from '../config';
import { Emitter } from '../util/emitter';
import { ProgressTracker } from '../util/progress';
import { PeerConnection } from '../webrtc/peer';
import type { SignalingClient } from '../webrtc/signaling';
import { FileChunker } from './chunker';
import {
  computeTotalChunks,
  decodeControl,
  encodeChunk,
  encodeControl,
  type FileMetadata,
} from './protocol';
import { Sha256 } from './sha256';
import type { SenderEvents } from './types';

export interface SenderOptions {
  chunkSize?: number;
}

/**
 * FileSender — drives the sending side of a transfer.
 *
 * Lifecycle:
 *   waiting-for-peer → negotiating → transferring → verifying → complete
 *
 * The file is streamed chunk-by-chunk over the DataChannel with backpressure so
 * memory stays flat. A separate streaming pass computes the whole-file SHA-256
 * for the receiver to verify against. Reconnects are handled transparently: the
 * receiver asks (via a `resume` control message) for exactly the chunks it is
 * missing, and only those are retransmitted.
 */
export class FileSender extends Emitter<SenderEvents> {
  private readonly peer: PeerConnection;
  private readonly chunker: FileChunker;
  private readonly meta: FileMetadata;
  private readonly progress: ProgressTracker;
  private readonly hashPromise: Promise<string>;

  private sending = false;
  private aborted = false;
  private completed = false;
  private ticker: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly signaling: SignalingClient,
    file: File,
    options: SenderOptions = {},
  ) {
    super();
    const chunkSize = options.chunkSize ?? DEFAULT_CHUNK_SIZE;
    this.chunker = new FileChunker(file, chunkSize);
    this.meta = {
      name: file.name,
      size: file.size,
      mime: file.type || 'application/octet-stream',
      chunkSize,
      totalChunks: computeTotalChunks(file.size, chunkSize),
    };
    this.progress = new ProgressTracker(file.size, this.meta.totalChunks);
    this.peer = new PeerConnection(signaling, true);
    this.hashPromise = this.computeHash();
    this.wire();
  }

  start(): void {
    this.emit('phase', 'waiting-for-peer');
    this.emit('meta', this.meta);
  }

  private wire(): void {
    // A receiver is present (initially or after a reconnect): (re)negotiate.
    this.signaling.on('peer-joined', ({ role }) => {
      if (role !== 'receiver' || this.completed) return;
      this.beginNegotiation();
    });
    this.signaling.on('joined', ({ peerPresent }) => {
      if (peerPresent && !this.completed) this.beginNegotiation();
    });
    this.signaling.on('peer-left', ({ role }) => {
      if (role === 'receiver') this.sending = false; // pause; resume on reconnect
    });

    this.peer.on('channelopen', () => {
      // Always (re)send metadata, then wait for the receiver to tell us whether
      // it wants the whole file ('ready') or only missing chunks ('resume').
      this.peer.send(encodeControl({ type: 'metadata', ...this.meta }));
      this.emit('phase', 'negotiating');
    });

    this.peer.on('message', (data) => {
      if (typeof data !== 'string') return; // sender receives only control msgs
      const msg = decodeControl(data);
      if (!msg) return;
      if (msg.type === 'ready') {
        const indices = msg.missingChunks ?? this.allIndices();
        void this.sendChunks(indices);
      } else if (msg.type === 'resume') {
        void this.sendChunks(msg.missingChunks);
      } else if (msg.type === 'cancel') {
        this.fail('Transfer cancelled by receiver', 'cancelled');
      }
    });

    this.peer.on('state', (s) => {
      if (s === 'connecting') this.emit('phase', 'connecting');
      if (s === 'failed') this.sending = false;
    });

    this.peer.on('error', (err) => this.fail(err.message));
  }

  private beginNegotiation(): void {
    if (this.completed) return;
    this.emit('phase', 'connecting');
    // Fresh RTCPeerConnection for each (re)negotiation cycle.
    this.peer.reset();
    void this.peer.start();
  }

  private allIndices(): number[] {
    return Array.from({ length: this.meta.totalChunks }, (_, i) => i);
  }

  private async sendChunks(indices: Iterable<number>): Promise<void> {
    if (this.sending || this.completed) return;
    this.sending = true;
    this.emit('phase', 'transferring');
    this.startTicker();

    try {
      for await (const { index, data } of this.chunker.iterateIndices(indices)) {
        if (this.aborted) return;
        if (!this.isChannelOpen()) {
          // Connection dropped mid-send; bail and wait for the resume request.
          this.sending = false;
          return;
        }
        if (this.peer.bufferedAmount > BUFFER_HIGH_WATER) {
          await this.peer.waitForDrain();
          if (!this.isChannelOpen() || this.aborted) {
            this.sending = false;
            return;
          }
        }
        this.peer.send(encodeChunk(index, data));
        this.progress.addChunk(data.byteLength);
      }

      // All requested chunks flushed. Finalize once the buffer fully drains.
      await this.peer.waitForDrain();
      this.emit('phase', 'verifying');
      const sha256 = await this.hashPromise;
      if (this.aborted || !this.isChannelOpen()) {
        this.sending = false;
        return;
      }
      this.peer.send(encodeControl({ type: 'complete', sha256 }));
      this.completed = true;
      this.stopTicker();
      this.emitProgress();
      this.emit('phase', 'complete');
      this.emit('done', { sha256 });
    } catch (err) {
      this.fail((err as Error).message);
    } finally {
      this.sending = false;
    }
  }

  /** Independent streaming pass to hash the whole file (order-stable). */
  private async computeHash(): Promise<string> {
    const hasher = new Sha256();
    for await (const { data } of this.chunker.iterate()) {
      hasher.update(new Uint8Array(data));
    }
    return hasher.digestHex();
  }

  private isChannelOpen(): boolean {
    return this.peer.dataChannel?.readyState === 'open';
  }

  private startTicker(): void {
    if (this.ticker) return;
    this.emitProgress();
    this.ticker = setInterval(() => this.emitProgress(), 1000);
  }

  private stopTicker(): void {
    if (this.ticker) clearInterval(this.ticker);
    this.ticker = null;
  }

  private emitProgress(): void {
    this.emit('progress', this.progress.snapshot());
  }

  private fail(message: string, phase: 'error' | 'cancelled' = 'error'): void {
    if (this.completed) return;
    this.stopTicker();
    this.emit('phase', phase);
    this.emit('error', { message });
  }

  cancel(): void {
    this.aborted = true;
    this.stopTicker();
    try {
      if (this.isChannelOpen()) this.peer.send(encodeControl({ type: 'cancel' }));
    } catch {
      /* ignore */
    }
    this.emit('phase', 'cancelled');
  }

  destroy(): void {
    this.aborted = true;
    this.stopTicker();
    this.peer.close();
    this.removeAll();
  }
}
